import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium",
    "transition-all duration-200",
    "disabled:pointer-events-none disabled:opacity-50",
    "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg focus-visible:ring-accent",
    "[&_svg]:size-4 [&_svg]:shrink-0"
  ].join(" "),
  {
    variants: {
      variant: {
        primary:   "bg-accent text-bg hover:bg-accent-hi shadow-[0_0_24px_-8px_var(--color-accent)]",
        outline:   "border border-line text-fg hover:border-line-2 hover:bg-bg-3",
        ghost:     "text-fg-2 hover:text-fg hover:bg-bg-3",
        secondary: "bg-bg-3 text-fg hover:bg-line-2",
        link:      "text-accent underline-offset-4 hover:underline rounded-md"
      },
      size: { sm: "h-9 px-4 text-xs", md: "h-10 px-5", lg: "h-12 px-7 text-base" }
    },
    defaultVariants: { variant: "primary", size: "md" }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />;
  }
);
Button.displayName = "Button";
